import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useParams } from 'react-router-dom';
import SubjectListComponent from '../../../components/subject/SubjectListComponent';
import { translatedMessage } from '../../../service/LanguageService';
import CompetitionToolbar from '../CompetitionToolbar';
import { CompetitionService } from '../../../service/CompetitionService';
import SubjectProposalsListComponent from '../../../components/subject/SubjectProposalsListComponent';
import SecurityService from '../../../service/SecurityService';

const SubjectListPage = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [competition, setCompetition] = useState(null);
    const [subjects, setSubjects] = useState([]);

    const [canViewSubjects, setCanViewSubjects] = useState(false);
    const [canEditSubjects, setCanEditSubjects] = useState(false);
    const [canEditSubjectProposals, setCanEditSubjectProposals] = useState(false);
    const [canExtractSubjects, setCanExtractSubjects] = useState(false);
    const [canInvalidateExtractedSubjects, setCanInvalidateExtractedSubjects] = useState(false);

    const toast = useRef(null);
    const { competitionIdParam } = useParams();
    const competitionService = useMemo(() => new CompetitionService(), []);

    useEffect(() => {
        setIsLoading(true);

        const getCompetition = async () => {
            await competitionService
                .getCompetition(competitionIdParam)
                .then((competitionData) => {
                    setCompetition(competitionData);
                })
                .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
        };

        getCompetition()
            .then((r) => getSubjects())
            .then((r) => checkCanViewSubjects())
            .then((r) => checkCanEditSubjects())
            .then((r) => checkCanEditSubjectProposals())
            .then((r) => checkCanExtractSubjects())
            .then((r) => checkCanInvalidateExtractedSubjects())
            .then((r) => setIsLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [competitionService, competitionIdParam]);

    const checkCanViewSubjects = async () => {
        setCanViewSubjects(await SecurityService.check('CAN_VIEW_SUBJECTS', competitionIdParam));
    };
    const checkCanEditSubjects = async () => {
        setCanEditSubjects(await SecurityService.check('CAN_EDIT_SUBJECTS', competitionIdParam));
    };
    const checkCanEditSubjectProposals = async () => {
        setCanEditSubjectProposals(await SecurityService.check('CAN_EDIT_SUBJECT_PROPOSALS', competitionIdParam));
    };
    const checkCanExtractSubjects = async () => {
        setCanExtractSubjects(await SecurityService.check('CAN_EXTRACT_SUBJECT', competitionIdParam));
    };
    const checkCanInvalidateExtractedSubjects = async () => {
        setCanInvalidateExtractedSubjects(await SecurityService.check('CAN_INVALIDATE_EXTRACTED_SUBJECTS', competitionIdParam));
    };

    const getSubjects = async () => {
        competitionService
            .getCompetitionSubjects(competitionIdParam)
            .then((_subjects) => setSubjects(_subjects))
            .then((r) => checkCanViewSubjects())
            .then((r) => checkCanEditSubjects())
            .then((r) => checkCanEditSubjectProposals())
            .then((r) => checkCanExtractSubjects())
            .then((r) => checkCanInvalidateExtractedSubjects())
            .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
    };

    if (isLoading) {
        return (
            <div className="w-full flex align-items-center">
                <Toast ref={toast} />
                <ProgressSpinner />
            </div>
        );
    } else {
        return (
            <>
                <Toast ref={toast} />
                <div className="grid h-full">
                    <div className="col-12">
                        <div className="card h-full">
                            <CompetitionToolbar competition={competition} code="SUBJECT" />

                            <SubjectListComponent
                                competition={competition}
                                subjects={subjects}
                                afterSave={getSubjects}
                                permissions={{
                                    canViewSubjects,
                                    canEditSubjects,
                                    canEditSubjectProposals,
                                    canExtractSubjects,
                                    canInvalidateExtractedSubjects
                                }}
                            />

                            <SubjectProposalsListComponent
                                competition={competition}
                                subjects={subjects}
                                afterSave={getSubjects}
                                permissions={{
                                    canViewSubjects,
                                    canEditSubjects,
                                    canEditSubjectProposals,
                                    canExtractSubjects,
                                    canInvalidateExtractedSubjects
                                }}
                            />
                        </div>
                    </div>
                </div>
            </>
        );
    }
};

export default SubjectListPage;
